/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { ActionNotifyRolesItem, defaultValue } from './ActionNotifyRoles';

const ActionNotifyRolesManager: ActionManagerFn<'ActionNotifyRoles'> = (
  platform: Platform,
): ActionManager<'ActionNotifyRoles'> => {
  return {
    ...baseActionManager(platform, 'ActionNotifyRoles'),
    defaultValue,
    props: {
      ...baseActionProps,
      roleAppUid: {
        label: 'Role App',
        type: 'AppUidChooser',
        helpText: (item: ActionNotifyRolesItem) => {
          if (item.props.roleAppUid === platform.appUid) {
            return 'Warning! When you migrate this page to another app, it will use the roles in this app. Leave this field blank otherwise.';
          }
          return 'Leave blank to use the current app. The roles in the selected app will be used.';
        },
        required: true,
      },
      roleCodes: {
        label: 'Role Codes',
        type: 'expression',
        returnType: async () => 'any',
        helpText:
          "Role codes to be notified. e.g. 'administrator' or ['administrator', 'user_administrator'].",
        required: true,
      },
      subject: {
        label: 'Subject',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Subject of the notification.',
        required: true,
      },
      body: {
        label: 'Body',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Body of the notification. (Markdown supported)',
        required: true,
      },
    },
    name: 'Notify Roles',
    icon: { iconName: 'comment-exclamation' },
    replaceableWith: ['ActionNotifyUsers'],
    description: `**Overview**<br/>
The Notify Roles action is a communication and alerting component designed to send bulk notifications to groups of users based on their assigned security roles. Instead of targeting specific individuals, this action broadens the scope to functional groups, ensuring that anyone holding a specific title (e.g., "Administrator" or "Approver") receives the message.<br/><br/>
This component is particularly useful for operational alerts, workflow handoffs, or system-wide announcements. It supports rich text formatting via Markdown for the message body and offers flexibility in how roles are targeted, accepting either single role codes or arrays of codes dynamically. It is designed to be easily interchangeable with the "Notify Users" action if the targeting strategy changes.

**Key Features**<br/>
* **Role-Based Targeting:** Delivers messages to every user currently assigned to the specified role(s), dynamically adapting as users are added or removed from those roles.
* **Flexible Input:** The roleCodes property accepts both a single string (e.g., 'admin') and an array of strings (e.g., ['admin', 'editor']), allowing for versatile targeting logic.
* **Rich Content:** Explicitly supports Markdown in the notification body, enabling developers to send formatted messages with bold text, lists, or links.
* **Cross-Application Scope:** Includes a roleAppUid property, allowing the action to target roles defined in a different application within the platform.

**Properties**<br/>
* **Role App (roleAppUid) (Required):** An AppUidChooser property.
  * *Function:* Selects the application context for the roles.
  * *Migration Note:* Leaving this blank is recommended for portability.
* **Role Codes (Required):** An expression property returning any (String or Array).
  * *Function:* Specifies the unique identifiers of the roles to notify.
* **Subject (Required):** An expression property returning a string.
  * *Function:* The headline or subject line of the notification.
* **Body (Required):** An expression property returning a string.
  * *Function:* The main content of the message. Supports Markdown formatting.

**Use Cases**<br/>
* **Workflow Handoffs:** Sending a "New Request Pending" notification to the "Approver" role whenever a user submits a form.
* **System Alerts:** Broadcasting a "Planned Maintenance" warning to all users with the "Administrator" role.
* **Policy Updates:** Notifying the "HR_Manager" group when a new candidate application is flagged for review.`,
  };
};

export default ActionNotifyRolesManager;
